<script setup>
/**
 * タイムチャート作成：タイムチャート本体（DEV-14983）
 *
 * - 縦＝スクリーン（名称・座席数）、横＝時刻。日ごとにタブで切り替え（日数が多いときはタブを横スクロール）
 * - 表示範囲は最も早い営業開始〜最も遅い営業終了（最大 30:00。24:00 以降は 25:30 のように表記）
 * - 上映回は横バー。色は「作品＋バージョン」ごとに分け、バー上に作品名・バージョン・開始〜終了を必ず表示
 * - 編集権限があればドラッグで開始時刻（5 分刻み）・スクリーンを変更、クリックで詳細編集
 * バーの長さは占有終了（終了＋バッファ）まで。違反チェックはバックエンドの結果を表示するだけ。
 */
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from "vue";
import { usePlanState } from "../use-plan";
import {
  TIME_MAX_MINUTES,
  calcShowTimes,
  colorKey,
  shortDateLabel,
  snapMinutes,
  toMinutes,
  toTime,
  versionLabel,
} from "../utils";

const props = defineProps({
  // 表示中の営業日（yyyy-MM-dd）
  activeDay: { type: String, default: "" },
  // 強調表示する上映回の _key（違反一覧から選択されたもの）
  highlightKey: { type: String, default: "" },
});
const emit = defineEmits(["update:activeDay", "open-show", "add-show"]);

// 行の高さ（px）
const ROW_HEIGHT = 64;
// 表示範囲の既定（営業時間が取れないとき）
const DEFAULT_RANGE = [6 * 60, 30 * 60];
// ドラッグ開始とみなす移動量（px）
const DRAG_THRESHOLD = 4;
// 表示倍率（1 分あたりの px）
const ZOOM_OPTIONS = [
  { label: "縮小", value: 1.2 },
  { label: "標準", value: 2 },
  { label: "拡大", value: 3 },
];
// どのスクリーンにも属さない上映回（スクリーン削除済み）を並べる行の ID
const ORPHAN_ROW_ID = "__orphan__";

const state = usePlanState();
const {
  canEdit, days, screens, shows, screenMap, movieMap, versionMap, colorMap, violations, violationsByShow,
} = state;

const zoom = ref(2);
const chartRef = ref(null);
const rowsRef = ref(null);

// ==================== 表示範囲・目盛り ====================

// 期間全体で共通の表示範囲（日を切り替えても横位置がずれないように）
const range = computed(() => {
  const starts = [];
  const ends = [];
  screens.value.forEach((s) => {
    const open = toMinutes(s.openTime);
    const close = toMinutes(s.closeTime);
    if (open !== null) starts.push(open);
    if (close !== null) ends.push(close);
  });
  shows.value.forEach((s) => {
    const t = calcShowTimes(s);
    if (t.start !== null) starts.push(t.start);
    if (t.occupyEnd !== null) ends.push(t.occupyEnd);
  });
  if (!starts.length || !ends.length) return DEFAULT_RANGE;
  const start = Math.max(0, Math.floor(Math.min(...starts) / 60) * 60);
  // 上映回が 30:00 を超える場合も見切れないよう、終了は上限で切らない
  const end = Math.ceil(Math.max(...ends) / 60) * 60;
  return [start, Math.max(end, start + 60)];
});

const trackWidth = computed(() => (range.value[1] - range.value[0]) * zoom.value);
const toX = (minutes) => (minutes - range.value[0]) * zoom.value;

// 1 時間ごとの目盛り（24 以降も 25・26 と表記）
const ticks = computed(() => {
  const list = [];
  for (let m = range.value[0]; m <= range.value[1]; m += 60) {
    list.push({ minutes: m, label: String(m / 60).padStart(2, "0"), left: toX(m) });
  }
  return list;
});

// ==================== 日タブ ====================

const dayTabs = computed(() => days.value.map((d) => ({
  date: d,
  label: shortDateLabel(d),
  count: shows.value.filter((s) => s.bizDate === d).length,
  violationCount: violations.value.filter((v) => {
    if (v.showKey) return shows.value.find((s) => s._key === v.showKey)?.bizDate === d;
    return v.bizDate === d;
  }).length,
})));

const selectDay = (d) => emit("update:activeDay", d);

// ---------- 日タブの横スクロール ----------
const dayTabsRef = ref(null);
// タブがはみ出しているか（はみ出しているときだけ左右ボタンを出す）
const dayOverflow = ref(false);
const canScrollLeft = ref(false);
const canScrollRight = ref(false);
let dayTabsObserver = null;

/**
 * スクロール位置からボタンの有効・無効を更新する
 */
const updateDayScroll = () => {
  const el = dayTabsRef.value;
  if (!el) return;
  dayOverflow.value = el.scrollWidth > el.clientWidth + 1;
  canScrollLeft.value = el.scrollLeft > 1;
  canScrollRight.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
};

const reduceMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * 左右ボタン：表示幅の 8 割ずつスクロール（direction: -1 左 / 1 右）
 */
const scrollDays = (direction) => {
  const el = dayTabsRef.value;
  if (!el) return;
  el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: reduceMotion() ? "auto" : "smooth" });
};

/**
 * 選択中の日タブが見切れていれば見える位置までスクロール（ページの縦スクロールは動かさない）
 */
const revealActiveDay = () => {
  const el = dayTabsRef.value;
  const tab = el?.querySelector(".day-tab.is-active");
  if (!el || !tab) return;
  // .day-tabs は position: relative なので offsetLeft はタブ列の左端からの位置
  const left = tab.offsetLeft;
  const right = left + tab.offsetWidth;
  if (left < el.scrollLeft) {
    el.scrollTo({ left, behavior: reduceMotion() ? "auto" : "smooth" });
  } else if (right > el.scrollLeft + el.clientWidth) {
    el.scrollTo({ left: right - el.clientWidth, behavior: reduceMotion() ? "auto" : "smooth" });
  }
};

watch(() => props.activeDay, () => nextTick(revealActiveDay));
// 日数・回数表示が変わるとタブ幅が変わるため、はみ出し判定をやり直す
watch(dayTabs, () => nextTick(updateDayScroll));

onMounted(() => {
  updateDayScroll();
  revealActiveDay();
  // ウィンドウ幅・パネル幅の変化に追従する（ResizeObserver 非対応環境はボタンなしでも横スクロールできる）
  if (typeof ResizeObserver !== "undefined" && dayTabsRef.value) {
    dayTabsObserver = new ResizeObserver(updateDayScroll);
    dayTabsObserver.observe(dayTabsRef.value);
  }
});

onBeforeUnmount(() => dayTabsObserver?.disconnect());

// ==================== 凡例 ====================

// 作品＋バージョンの凡例（その日の回数付き）
const legend = computed(() => {
  const list = [];
  state.movies.value.forEach((m) => (m.versions || []).forEach((v) => {
    const key = colorKey(m.schedMovieId, v.versionId);
    list.push({
      key,
      movie: m,
      version: v,
      color: colorMap.value.get(key),
      count: shows.value.filter((s) => s.bizDate === props.activeDay
        && s.schedMovieId === m.schedMovieId && s.versionId === v.versionId).length,
    });
  }));
  return list;
});

// 凡例の開閉（作品・バージョンが多いと縦に長くなるため既定は閉じる）
const legendOpen = ref(false);

// ==================== ドラッグ ====================

// { key, startX, startY, origStart, origScreenId, start, screenId, moved }
const drag = ref(null);
// ドラッグ直後の click を無視する
let suppressClick = false;

const rows = computed(() => {
  const list = screens.value.map((s) => ({ id: s.screenId, screen: s }));
  const hasOrphan = shows.value.some((s) => s.bizDate === props.activeDay && !screenMap.value.has(s.screenId));
  if (hasOrphan) list.push({ id: ORPHAN_ROW_ID, screen: null });
  return list;
});

/**
 * ポインタの縦位置 → スクリーン行（実在するスクリーンのみ。削除済み行へは移動不可）
 */
const screenIdAt = (clientY) => {
  const rect = rowsRef.value?.getBoundingClientRect();
  if (!rect || !screens.value.length) return null;
  const index = Math.min(screens.value.length - 1, Math.max(0, Math.floor((clientY - rect.top) / ROW_HEIGHT)));
  return screens.value[index].screenId;
};

const onPointerMove = (e) => {
  const d = drag.value;
  if (!d) return;
  const dx = e.clientX - d.startX;
  const dy = e.clientY - d.startY;
  if (!d.moved && Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
  d.moved = true;
  const start = snapMinutes(d.origStart + dx / zoom.value);
  d.start = Math.min(TIME_MAX_MINUTES, Math.max(0, start));
  d.screenId = screenIdAt(e.clientY) ?? d.origScreenId;
};

const endDrag = (commit) => {
  const d = drag.value;
  window.removeEventListener("pointermove", onPointerMove);
  window.removeEventListener("pointerup", onPointerUp);
  window.removeEventListener("pointercancel", onPointerCancel);
  window.removeEventListener("keydown", onDragKeydown);
  drag.value = null;
  if (!d || !d.moved) return;
  suppressClick = true;
  if (commit && (d.start !== d.origStart || d.screenId !== d.origScreenId)) {
    state.updateShow(d.key, { startTime: toTime(d.start), screenId: d.screenId });
  }
};

function onPointerUp() {
  endDrag(true);
}

function onPointerCancel() {
  endDrag(false);
}

// ドラッグ中に Esc で取り消し
function onDragKeydown(e) {
  if (e.key === "Escape") endDrag(false);
}

const onBarPointerDown = (e, show) => {
  if (!canEdit || e.button !== 0) return;
  const start = toMinutes(show.startTime);
  if (start === null) return;
  // 削除済みスクリーンの上映回は、ドラッグ先が決まるまで先頭のスクリーン行をプレビュー位置にする
  const previewScreenId = screenMap.value.has(show.screenId) ? show.screenId : screens.value[0]?.screenId;
  drag.value = {
    key: show._key,
    startX: e.clientX,
    startY: e.clientY,
    origStart: start,
    origScreenId: show.screenId,
    start,
    screenId: previewScreenId ?? show.screenId,
    moved: false,
  };
  window.addEventListener("pointermove", onPointerMove);
  window.addEventListener("pointerup", onPointerUp);
  window.addEventListener("pointercancel", onPointerCancel);
  window.addEventListener("keydown", onDragKeydown);
};

const onBarClick = (show) => {
  if (suppressClick) {
    suppressClick = false;
    return;
  }
  emit("open-show", show._key);
};

onBeforeUnmount(() => endDrag(false));

// ==================== バー ====================

/**
 * 表示用のバー情報（ドラッグ中はプレビュー位置）
 */
const toBar = (show) => {
  const d = drag.value?.key === show._key && drag.value.moved ? drag.value : null;
  const base = calcShowTimes(show);
  const start = d ? d.start : base.start;
  const shift = start - base.start;
  const end = base.end + shift;
  const occupyEnd = base.occupyEnd + shift;
  const preroll = (show.adMinute || 0) + (show.trailerMinute || 0);
  const key = colorKey(show.schedMovieId, show.versionId);
  const movie = movieMap.value.get(show.schedMovieId);
  const version = versionMap.value.get(key);
  const list = violationsByShow.value.get(show._key) || [];
  const title = movie?.title || "削除された作品";
  const verText = version ? versionLabel(version) : "削除されたバージョン";
  const timeText = `${toTime(start)}〜${toTime(end)}`;
  const tooltip = [
    title,
    verText,
    `${timeText}（占有 〜${toTime(occupyEnd)}）`,
    `CM ${show.adMinute}分 / 予告 ${show.trailerMinute}分 / 本編 ${show.featureMinute}分 / バッファ ${show.bufferMinute}分`,
    show.estSeats != null ? `予測動員 ${show.estSeats}人` : "予測動員 算出不可",
    ...list.map((v) => `⚠ ${v.typeLabel}：${v.message || ""}`),
  ].join("\n");
  return {
    show,
    key: show._key,
    rowId: d ? d.screenId : (screenMap.value.has(show.screenId) ? show.screenId : ORPHAN_ROW_ID),
    left: toX(start),
    width: Math.max(4, (occupyEnd - start) * zoom.value),
    prerollWidth: preroll * zoom.value,
    bufferWidth: (show.bufferMinute || 0) * zoom.value,
    color: colorMap.value.get(key) || "var(--sp-muted)",
    title,
    verText,
    timeText,
    tooltip,
    violated: list.length > 0,
    dragging: !!d,
  };
};

// 行 ID → その日のバー
const barsByRow = computed(() => {
  const map = new Map(rows.value.map((r) => [r.id, []]));
  shows.value
    .filter((s) => s.bizDate === props.activeDay)
    .forEach((s) => {
      const bar = toBar(s);
      if (!map.has(bar.rowId)) map.set(bar.rowId, []);
      map.get(bar.rowId).push(bar);
    });
  return map;
});

/**
 * 営業時間外の網掛け（行ごと）
 */
const closedAreas = (screen) => {
  if (!screen) return [];
  const open = toMinutes(screen.openTime);
  const close = toMinutes(screen.closeTime);
  if (open === null || close === null) return [];
  const areas = [];
  if (open > range.value[0]) areas.push({ left: 0, width: toX(open) });
  if (close < range.value[1]) areas.push({ left: toX(close), width: trackWidth.value - toX(close) });
  return areas;
};

// ドラッグ中の時刻ガイド
const dragGuide = computed(() => {
  const d = drag.value;
  if (!d?.moved) return null;
  return { left: toX(d.start), label: toTime(d.start) };
});

// ==================== 強調表示 ====================

watch(
  () => props.highlightKey,
  async (key) => {
    if (!key) return;
    await nextTick();
    const el = chartRef.value?.querySelector(`[data-show-key="${key}"]`);
    el?.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
  },
);
</script>

<template>
  <div class="time-chart">
    <!-- 日タブ・表示倍率・上映回追加（1 行。日タブははみ出したら横スクロール） -->
    <div class="chart-toolbar">
      <div class="day-nav">
        <button
          v-if="dayOverflow"
          type="button"
          class="day-scroll"
          :disabled="!canScrollLeft"
          aria-label="前の日を表示"
          @click="scrollDays(-1)"
        >
          <el-icon><ArrowLeft /></el-icon>
        </button>
        <div ref="dayTabsRef" class="day-tabs" role="tablist" aria-label="営業日" @scroll.passive="updateDayScroll">
          <button
            v-for="tab in dayTabs"
            :key="tab.date"
            type="button"
            role="tab"
            :aria-selected="tab.date === activeDay"
            class="day-tab"
            :class="{ 'is-active': tab.date === activeDay }"
            @click="selectDay(tab.date)"
          >
            <span class="day-tab__label">{{ tab.label }}</span>
            <span class="day-tab__count">{{ tab.count }}回</span>
            <span v-if="tab.violationCount" class="day-tab__warn" :aria-label="`違反${tab.violationCount}件`">
              ⚠ {{ tab.violationCount }}
            </span>
          </button>
        </div>
        <button
          v-if="dayOverflow"
          type="button"
          class="day-scroll"
          :disabled="!canScrollRight"
          aria-label="次の日を表示"
          @click="scrollDays(1)"
        >
          <el-icon><ArrowRight /></el-icon>
        </button>
      </div>
      <div class="toolbar-right">
        <el-radio-group v-model="zoom" size="small" aria-label="表示倍率">
          <el-radio-button v-for="z in ZOOM_OPTIONS" :key="z.value" :label="z.value">{{ z.label }}</el-radio-button>
        </el-radio-group>
        <el-button
          v-if="canEdit"
          v-hasPermi="['dp:scheduleAi:plan:edit']"
          size="small"
          type="primary"
          plain
          @click="emit('add-show', {})"
        >
          上映回を追加
        </el-button>
      </div>
    </div>

    <!-- 凡例（クリックでその作品・バージョンの上映回を追加） -->
    <div v-if="legend.length" class="legend-toggle">
      <el-button
        link
        type="primary"
        :aria-expanded="legendOpen"
        aria-controls="time-chart-legend"
        @click="legendOpen = !legendOpen"
      >
        {{ legendOpen ? "凡例を閉じる" : "凡例を表示" }}（作品・バージョン {{ legend.length }}）
        <el-icon class="el-icon--right">
          <ArrowUp v-if="legendOpen" />
          <ArrowDown v-else />
        </el-icon>
      </el-button>
      <span v-if="legendOpen && canEdit" class="legend-toggle__hint">クリックでその作品・バージョンの上映回を追加</span>
    </div>
    <ul v-show="legendOpen" id="time-chart-legend" class="legend" aria-label="作品・バージョンの凡例">
      <li v-for="item in legend" :key="item.key">
        <component
          :is="canEdit ? 'button' : 'span'"
          :type="canEdit ? 'button' : undefined"
          class="legend-item"
          :class="{ 'is-clickable': canEdit }"
          :style="{ '--bar-color': item.color }"
          :title="canEdit ? 'クリックでこの作品・バージョンの上映回を追加' : undefined"
          @click="canEdit && emit('add-show', { schedMovieId: item.movie.schedMovieId, versionId: item.version.versionId })"
        >
          <span class="legend-item__swatch" />
          <span class="legend-item__title">{{ item.movie.title }}</span>
          <span class="legend-item__ver">{{ versionLabel(item.version) }}</span>
          <span class="legend-item__count">{{ item.count }}回</span>
        </component>
      </li>
    </ul>

    <el-empty v-if="!screens.length" description="スクリーンが登録されていません" :image-size="64" />

    <div v-else ref="chartRef" class="chart-scroll">
      <div class="chart" :style="{ width: `calc(var(--label-width) + ${trackWidth}px)` }">
        <!-- 時刻目盛り -->
        <div class="chart-head">
          <div class="label-cell label-cell--head">スクリーン</div>
          <div class="track track--head" :style="{ width: `${trackWidth}px` }">
            <span
              v-for="t in ticks"
              :key="t.minutes"
              class="tick"
              :class="{ 'is-next-day': t.minutes >= 24 * 60 }"
              :style="{ left: `${t.left}px` }"
            >
              {{ t.label }}
            </span>
          </div>
        </div>

        <!-- スクリーン行 -->
        <div ref="rowsRef" class="chart-rows" :style="{ '--hour-width': `${zoom * 60}px` }">
          <div v-for="row in rows" :key="row.id" class="chart-row" :style="{ height: `${ROW_HEIGHT}px` }">
            <div class="label-cell">
              <template v-if="row.screen">
                <span class="label-cell__name">{{ row.screen.name }}</span>
                <span class="label-cell__meta">{{ row.screen.maxSeats }}席 / {{ row.screen.openTime }}–{{ row.screen.closeTime }}</span>
              </template>
              <template v-else>
                <span class="label-cell__name warn-text">削除されたスクリーン</span>
                <span class="label-cell__meta">ドラッグで移動してください</span>
              </template>
            </div>
            <div
              class="track"
              :class="{ 'is-drop-target': drag?.moved && drag.screenId === row.id }"
              :style="{ width: `${trackWidth}px` }"
            >
              <span
                v-for="(area, i) in closedAreas(row.screen)"
                :key="i"
                class="closed-area"
                :style="{ left: `${area.left}px`, width: `${area.width}px` }"
                aria-hidden="true"
              />
              <button
                v-for="bar in barsByRow.get(row.id) || []"
                :key="bar.key"
                type="button"
                class="bar"
                :class="{
                  'is-violated': bar.violated,
                  'is-highlight': bar.key === highlightKey,
                  'is-dragging': bar.dragging,
                  'is-editable': canEdit,
                }"
                :data-show-key="bar.key"
                :style="{ left: `${bar.left}px`, width: `${bar.width}px`, '--bar-color': bar.color }"
                :title="bar.tooltip"
                :aria-label="bar.tooltip"
                @pointerdown="onBarPointerDown($event, bar.show)"
                @click="onBarClick(bar.show)"
              >
                <span class="bar__preroll" :style="{ width: `${bar.prerollWidth}px` }" aria-hidden="true" />
                <span class="bar__buffer" :style="{ width: `${bar.bufferWidth}px` }" aria-hidden="true" />
                <span class="bar__text">
                  <span class="bar__title">
                    <span v-if="bar.violated" class="bar__warn" aria-hidden="true">⚠</span>{{ bar.title }}
                  </span>
                  <span class="bar__ver">{{ bar.verText }}</span>
                  <span class="bar__time">{{ bar.timeText }}</span>
                </span>
              </button>
            </div>
          </div>

          <!-- ドラッグ中の開始時刻ガイド -->
          <div
            v-if="dragGuide"
            class="drag-guide"
            :style="{ left: `calc(var(--label-width) + ${dragGuide.left}px)` }"
            aria-hidden="true"
          >
            <span class="drag-guide__label">{{ dragGuide.label }}</span>
          </div>
        </div>
      </div>
    </div>

    <p class="hint chart-hint">
      バーの長さはバッファ終了まで（斜線＝バッファ、薄い部分＝CM・予告）。
      <template v-if="canEdit">ドラッグで開始時刻（5分刻み）・スクリーンを変更、クリックで詳細を編集できます。Escでドラッグを取り消し。</template>
    </p>
  </div>
</template>

<style lang="less" scoped>
.time-chart {
  --label-width: 150px;

  min-width: 0;
}

/* ---------- ツールバー ---------- */
.chart-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px 16px;
  padding: 12px 18px 8px;
}

// 日タブ＋左右スクロールボタン。残り幅を使い、はみ出した分は横スクロール
.day-nav {
  display: flex;
  flex: 1;
  align-items: center;
  gap: 4px;
  min-width: 0;
}

.day-tabs {
  position: relative;
  display: flex;
  flex: 1;
  gap: 4px;
  min-width: 0;
  overflow-x: auto;
  scroll-behavior: auto;
  // スクロールバーは出さずにトラックパッド・スワイプ・左右ボタンで動かす
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.day-scroll {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 32px;
  padding: 0;
  border: 1px solid var(--sp-border);
  border-radius: var(--sp-radius);
  color: var(--sp-text-2);
  background: var(--sp-panel);
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: var(--sp-brand);
    color: var(--sp-brand);
  }

  &:focus-visible {
    outline: 2px solid var(--sp-brand);
    outline-offset: 1px;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
}

.day-tab {
  display: inline-flex;
  flex: none;
  white-space: nowrap;
  align-items: baseline;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid var(--sp-border);
  border-radius: var(--sp-radius);
  font: inherit;
  font-size: 13px;
  color: var(--sp-text-2);
  background: var(--sp-panel);
  cursor: pointer;

  &:hover {
    border-color: var(--sp-brand);
  }

  &:focus-visible {
    outline: 2px solid var(--sp-brand);
    outline-offset: 1px;
  }

  &.is-active {
    border-color: var(--sp-brand);
    font-weight: 700;
    color: var(--sp-text);
    background: var(--sp-tint-strong);
  }

  &__count {
    font-size: 11px;
    font-variant-numeric: tabular-nums;
    color: var(--sp-muted);
  }

  &__warn {
    font-size: 11px;
    font-weight: 700;
    color: var(--el-color-danger);
  }
}

.toolbar-right {
  display: flex;
  flex: none;
  align-items: center;
  gap: 8px;
}

/* ---------- 凡例 ---------- */
.legend-toggle {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 12px;
  padding: 0 18px 8px;

  &__hint {
    font-size: 12px;
    color: var(--sp-muted);
  }
}

.legend {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0;
  padding: 0 18px 10px;
  list-style: none;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px;
  border: 1px solid var(--sp-border);
  border-radius: 4px;
  font: inherit;
  font-size: 12px;
  color: var(--sp-text);
  background: color-mix(in srgb, var(--bar-color) 12%, var(--sp-panel));

  &.is-clickable {
    cursor: pointer;

    &:hover,
    &:focus-visible {
      border-color: var(--bar-color);
      outline: none;
    }
  }

  &__swatch {
    width: 10px;
    height: 10px;
    border-radius: 2px;
    background: var(--bar-color);
  }

  &__title {
    font-weight: 600;
  }

  &__ver,
  &__count {
    color: var(--sp-text-2);
  }

  &__count {
    font-variant-numeric: tabular-nums;
  }
}

/* ---------- チャート ---------- */
.chart-scroll {
  overflow-x: auto;
  border-top: 1px solid var(--sp-border);
  border-bottom: 1px solid var(--sp-border);
}

.chart {
  position: relative;
}

.chart-head,
.chart-row {
  display: flex;
}

.chart-head {
  position: sticky;
  top: 0;
  z-index: 3;
  height: 28px;
  border-bottom: 1px solid var(--sp-border);
  background: var(--sp-panel);
}

.chart-rows {
  position: relative;
}

.chart-row + .chart-row {
  border-top: 1px solid var(--sp-border);
}

.label-cell {
  position: sticky;
  left: 0;
  z-index: 2;
  display: flex;
  flex: none;
  flex-direction: column;
  justify-content: center;
  width: var(--label-width);
  padding: 0 12px;
  box-sizing: border-box;
  border-right: 1px solid var(--sp-border);
  background: var(--sp-panel);

  &--head {
    font-size: 12px;
    color: var(--sp-muted);
  }

  &__name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 13px;
    font-weight: 600;
  }

  &__meta {
    font-size: 11px;
    font-variant-numeric: tabular-nums;
    color: var(--sp-muted);
  }
}

.track {
  position: relative;
  flex: none;
  // 1 時間ごとの縦罫線
  background-image: linear-gradient(to right, var(--sp-border) 1px, transparent 1px);
  background-size: var(--hour-width) 100%;

  &--head {
    background: none;
  }

  &.is-drop-target {
    background-color: var(--sp-tint);
  }
}

.tick {
  position: absolute;
  top: 6px;
  transform: translateX(-50%);
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  color: var(--sp-muted);

  &.is-next-day {
    color: var(--sp-brand);
  }
}

.closed-area {
  position: absolute;
  top: 0;
  bottom: 0;
  background: repeating-linear-gradient(
    -45deg,
    color-mix(in srgb, var(--sp-muted) 14%, transparent) 0 4px,
    transparent 4px 8px
  );
  pointer-events: none;
}

/* ---------- 上映回バー ---------- */
.bar {
  position: absolute;
  top: 5px;
  bottom: 5px;
  display: block;
  overflow: hidden;
  margin: 0;
  padding: 0;
  border: 1px solid color-mix(in srgb, var(--bar-color) 55%, var(--sp-panel));
  border-left: 4px solid var(--bar-color);
  border-radius: 4px;
  font: inherit;
  text-align: left;
  color: var(--sp-text);
  background: color-mix(in srgb, var(--bar-color) 20%, var(--sp-panel));
  cursor: pointer;
  touch-action: none;
  user-select: none;

  &.is-editable {
    cursor: grab;
  }

  &:focus-visible {
    outline: 2px solid var(--sp-text);
    outline-offset: 1px;
  }

  &.is-violated {
    border-color: var(--el-color-danger);
    box-shadow: inset 0 0 0 1px var(--el-color-danger);
  }

  &.is-highlight {
    z-index: 1;
    outline: 3px solid var(--el-color-danger);
    outline-offset: 2px;
    animation: bar-pulse 1s ease-in-out 3;
  }

  &.is-dragging {
    z-index: 2;
    cursor: grabbing;
    opacity: 0.85;
    box-shadow: 0 4px 12px rgb(0 0 0 / 20%);
  }

  &__preroll {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    background: color-mix(in srgb, var(--sp-panel) 45%, transparent);
  }

  &__buffer {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    background: repeating-linear-gradient(
      -45deg,
      color-mix(in srgb, var(--bar-color) 35%, transparent) 0 3px,
      transparent 3px 6px
    );
  }

  &__text {
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 2px 6px;
    line-height: 1.3;
    white-space: nowrap;
  }

  &__title,
  &__ver,
  &__time {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__title {
    font-size: 12px;
    font-weight: 700;
  }

  &__ver,
  &__time {
    font-size: 11px;
  }

  &__time {
    font-variant-numeric: tabular-nums;
  }

  &__warn {
    margin-right: 2px;
    color: var(--el-color-danger);
  }
}

@keyframes bar-pulse {
  50% {
    outline-color: transparent;
  }
}

.drag-guide {
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 3;
  width: 0;
  border-left: 1px dashed var(--sp-brand);
  pointer-events: none;

  &__label {
    position: absolute;
    top: -22px;
    left: 0;
    transform: translateX(-50%);
    padding: 0 4px;
    border-radius: 3px;
    font-size: 11px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: #fff;
    background: var(--sp-brand);
  }
}

.chart-hint {
  padding: 8px 18px 12px;
  box-sizing: border-box;
}

@media (max-width: 768px) {
  .time-chart {
    --label-width: 100px;
  }

  // スマホでは日タブを 1 行目、表示倍率・追加ボタンを 2 行目にする
  .chart-toolbar {
    flex-wrap: wrap;
  }

  .day-nav {
    flex-basis: 100%;
  }
}
</style>
